import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, FieldErrors, useFormContext } from 'react-hook-form';
import isURL from 'validator/lib/isURL';
import {
  maxLengthRule,
  postalCodePatternRule,
  PREVIOUSLY_ENTERED,
  requiredText,
  taxIdPatternRule,
  trimString,
} from '#/Scripts/shared/form/validation';
import { COMPANY_TYPE_OPTIONS, YES_NO_OPTIONS } from '#/Scripts/shared/signupOptions';
import { getStateProvinceOptions } from '#/Scripts/shared/stateOptions';
import Button from '#sign-up/components/common/Button/Button';
import { ButtonVariants } from '#sign-up/components/common/Button/buttonTypes';
import { IconButton } from '#sign-up/components/common/Button/IconButton';
import FormTextField from '#sign-up/components/common/FormTextField/FormTextField';
import Select from '#sign-up/components/common/Select/Select';
import { Modal } from '#sign-up/components/Modal/Modal';
import {
  AccountCategory,
  AccountCategoryDisplayNames,
  Country,
  CountryDisplayNames,
  enumToDropdownOptions,
  LegalEntityType,
  LegalEntityTypeDisplayNames,
  SignupFormValues,
} from '#sign-up/components/SignUp/types';

export const CompanyInformationForm = () => {
  const form = useFormContext<SignupFormValues>();
  const countryId = form.watch('CountryId');
  const isCanada = countryId === Country.Canada;
  const isPublicEntity = form.watch('IsPublicEntity');
  const taxId = form.watch('TaxId');
  const stockExchange = form.watch('StockExchange');
  const stockTicker = form.watch('StockTicker');
  const taxIdRef = useRef<HTMLInputElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Store previous values to restore when switching company types
  const previousStockExchangeRef = useRef<string | null>(null);
  const previousStockTickerRef = useRef<string | null>(null);
  const previousTaxIdRef = useRef<string | null>(null);
  const previousCountryIdRef = useRef(countryId);

  useEffect(() => {
    taxIdRef.current ??= document.querySelector<HTMLInputElement>('input[name="TaxId"]');
  }, []);

  // Reset State when Country changes
  useEffect(() => {
    if (previousCountryIdRef.current !== undefined && previousCountryIdRef.current !== countryId) {
      form.setValue('State', '', { shouldValidate: false });
    }
    previousCountryIdRef.current = countryId;
  }, [countryId]);

  useEffect(() => {
    if (isPublicEntity === true) {
      // Restore stock fields if they were previously entered
      if (previousStockExchangeRef.current !== null) {
        form.setValue('StockExchange', previousStockExchangeRef.current, { shouldValidate: false });
      }
      if (previousStockTickerRef.current !== null) {
        form.setValue('StockTicker', previousStockTickerRef.current, { shouldValidate: false });
      }
      // Save current TaxId before clearing
      const currentTaxId = form.getValues('TaxId');
      if (currentTaxId && currentTaxId !== '') {
        previousTaxIdRef.current = currentTaxId;
      }
    } else if (isPublicEntity === false) {
      // Save current stock fields before clearing
      if (stockExchange && stockExchange !== '') {
        previousStockExchangeRef.current = stockExchange;
      }
      if (stockTicker && stockTicker !== '') {
        previousStockTickerRef.current = stockTicker;
      }
      // Clear stock fields
      form.setValue('StockExchange', '', { shouldValidate: false });
      form.setValue('StockTicker', '', { shouldValidate: false });
      // Restore TaxId if it was previously entered
      if (previousTaxIdRef.current !== null) {
        form.setValue('TaxId', previousTaxIdRef.current, { shouldValidate: false });
      }
    }
  }, [isPublicEntity]);

  // Track previous country to detect user-initiated changes and clear payment fields
  const prevIsCanadaRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (prevIsCanadaRef.current === null) {
      // Initial render — record starting state without clearing
      prevIsCanadaRef.current = isCanada;
      return;
    }
    if (prevIsCanadaRef.current === isCanada) return;
    prevIsCanadaRef.current = isCanada;

    // Clear shared trust bank fields regardless of direction
    form.setValue('TrustAccountTypeId', null, { shouldValidate: false });
    form.setValue('TrustRoutingNumber', '', { shouldValidate: false });
    form.setValue('InstitutionNumber', '', { shouldValidate: false });
    form.setValue('TransitNumber', '', { shouldValidate: false });
    form.setValue('TrustAccountNumber', '', { shouldValidate: false });

    if (isCanada) {
      // Switched to Canada — clear US operational bank fields
      form.setValue('OperationalAccountTypeId', null, { shouldValidate: false });
      form.setValue('OperationalRoutingNumber', '', { shouldValidate: false });
      form.setValue('OperationalAccountNumber', '', { shouldValidate: false });
    } else {
      // Switched to US — clear Canada credit card fields and also reset the operational bank fields
      // so any prefilled or mirrored values from the Canada flow don't bleed through
      form.setValue('OperationalAccountTypeId', null, { shouldValidate: false });
      form.setValue('OperationalRoutingNumber', '', { shouldValidate: false });
      form.setValue('OperationalAccountNumber', '', { shouldValidate: false });
      form.setValue('CreditCardName', '', { shouldValidate: false });
      form.setValue('CreditCardNumber', '', { shouldValidate: false });
      form.setValue('CreditCardMonth', '', { shouldValidate: false });
      form.setValue('CreditCardYear', '', { shouldValidate: false });
      form.setValue('CreditCardCvc', '', { shouldValidate: false });
      form.setValue('CreditCardPostalCode', '', { shouldValidate: false });
    }
  }, [isCanada]);

  const toggleModal = () => setIsOpen((prev) => !prev);

  const handleReadOnlyClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    (e.target as HTMLInputElement).blur();
  }, []);

  const handleReadOnlyFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.target.blur();
  }, []);

  const handleEditTaxId = useCallback(() => {
    form.setValue('TaxId', '');
    taxIdRef.current?.focus();
  }, [form]);

  const handleTaxIdBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const value = e.target.value.trim();
      form.setValue('TaxId', value, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [form]
  );

  const renderEditIcon = useCallback(
    (isReadonly: boolean, onClick: () => void, testId: string) =>
      isReadonly ? (
        <IconButton
          className="edit-icon"
          iconName="edit"
          title="Edit"
          onClick={onClick}
          variant={ButtonVariants.outline}
          data-testid={testId}
          style={{ border: 'none', boxShadow: 'none' }}
        />
      ) : undefined,
    []
  );

  const entityTypeOptions = enumToDropdownOptions(LegalEntityType, LegalEntityTypeDisplayNames).sort((a, b) => {
    return a.Text.localeCompare(b.Text);
  });
  const countryOptions = enumToDropdownOptions(Country, CountryDisplayNames);
  const categoryOptions = enumToDropdownOptions(AccountCategory, AccountCategoryDisplayNames);

  return (
    <div className="signup-form company-information">
      <div className="signup-form-header">
        <h1>Company Information</h1>
        <p>
          Please ensure your Company Name matches exactly what is shown on your W9. PO Box cannot be used for your
          company address.
        </p>
      </div>

      <div className="account-conditions">
        <Button onClick={toggleModal} variant={ButtonVariants.link}>
          What if I don&apos;t have a registered business?
        </Button>
      </div>

      <Modal
        isOpen={isOpen}
        handleClose={toggleModal}
        title="What if I don't have a registered business?"
        footer={<Button onClick={toggleModal}>Close</Button>}
      >
        <div>
          <p>
            Don&apos;t worry! If you don&apos;t have a registered business or are a sole proprietor, simply use your
            name, address, and Social Security Number (SSN) if you don&apos;t have an EIN.
          </p>
        </div>
      </Modal>

      <div className="signup-form-row">
        <FormTextField
          label="Company Name"
          {...form.register('CompanyName', {
            ...requiredText('Company Name'),
            ...maxLengthRule(80, 'Company Name'),
            ...trimString,
          })}
          errors={form.formState.errors.CompanyName}
          className="with-subtext"
          autoComplete="organization"
          placeholder="Must match your W9"
        />
        <FormTextField
          label="DBA (Optional)"
          {...form.register('DoingBusinessAs', {
            ...maxLengthRule(80, 'DBA'),
            ...trimString,
          })}
          errors={form.formState.errors.DoingBusinessAs}
          className="with-subtext"
          autoComplete="family-name"
        />
      </div>
      <div className="signup-form-row">
        <FormTextField
          label="Address 1"
          {...form.register('StreetAddress', {
            ...requiredText('Address 1'),
            ...maxLengthRule(100, 'Address 1'),
            ...trimString,
          })}
          errors={form.formState.errors.StreetAddress}
          className="with-subtext"
          autoComplete="street-address"
          placeholder="No PO Boxes"
        />
        <FormTextField
          label="Address 2"
          {...form.register('Suite', {
            ...maxLengthRule(50, 'Address 2'),
            ...trimString,
          })}
          errors={form.formState.errors.Suite}
          className="with-subtext"
          autoComplete="address-line2"
        />
      </div>
      <div className="signup-form-row">
        <div className="address-fields">
          <div className="field wide">
            <Select
              label="Country"
              placeholder="Select..."
              options={countryOptions}
              {...form.register('CountryId', {
                ...requiredText('Country'),
                ...trimString,
                setValueAs: (value: string): Country | undefined => (value ? Number.parseInt(value, 10) : undefined),
              })}
              errors={form.formState.errors}
              className="with-subtext"
            />
          </div>
          <div className="field wide">
            <FormTextField
              label="City"
              {...form.register('City', {
                ...requiredText('City'),
                ...maxLengthRule(100, 'City'),
                ...trimString,
              })}
              errors={form.formState.errors.City}
              className="with-subtext"
              autoComplete="address-level2"
            />
          </div>
        </div>
        <div className="address-fields">
          <div className="field narrow">
            <Select
              label={isCanada ? 'Province' : 'State'}
              placeholder="Select..."
              options={getStateProvinceOptions(isCanada).map((x) => ({ Text: x, Value: x }))}
              {...form.register('State', {
                required: `${isCanada ? 'Province' : 'State'} is required.`,
                ...trimString,
              })}
              className="with-subtext"
              errors={form.formState.errors}
            />
          </div>
          <div className="field narrow">
            <FormTextField
              label="Postal Code"
              {...form.register('PostalCode', {
                ...requiredText('Postal Code'),
                ...postalCodePatternRule(isCanada),
                ...trimString,
              })}
              errors={form.formState.errors.PostalCode}
              className="with-subtext"
              autoComplete="postal-code"
            />
          </div>
        </div>
      </div>
      <div className="signup-form-row">
        <FormTextField
          label={isCanada ? 'Social Insurance Number' : 'Tax ID / EIN'}
          className="with-subtext"
          placeholder={taxId === PREVIOUSLY_ENTERED ? 'Previously entered' : undefined}
          readOnly={taxId === PREVIOUSLY_ENTERED}
          onClick={taxId === PREVIOUSLY_ENTERED ? handleReadOnlyClick : undefined}
          onFocus={taxId === PREVIOUSLY_ENTERED ? handleReadOnlyFocus : undefined}
          icon={renderEditIcon(taxId === PREVIOUSLY_ENTERED, handleEditTaxId, 'edit-tax-id')}
          errors={form.formState.errors.TaxId}
          {...form.register('TaxId', {
            validate: (value) => {
              // Allow previously entered sentinel (value not changed)
              if (value === PREVIOUSLY_ENTERED) return true;
              if (!value || value.trim().length === 0) {
                return `${isCanada ? 'Social Insurance Number' : 'Tax ID'} is required.`;
              }
              // Apply pattern validation
              const patternRule = taxIdPatternRule(isCanada ? 'Social Insurance Number' : 'Tax ID', isCanada);
              const pattern = patternRule.pattern as { value: RegExp; message: string };
              return pattern.value.test(value) || pattern.message;
            },
            ...trimString,
            onBlur: handleTaxIdBlur,
          })}
        />
        <Controller
          control={form.control}
          name="LegalEntityTypeId"
          rules={{
            ...requiredText('Entity Type'),
          }}
          render={({ field }) => (
            <Select
              label="Entity Type"
              placeholder="Select..."
              options={entityTypeOptions}
              value={field.value === undefined || field.value === null ? '' : String(field.value)}
              onChange={(e) => {
                const v = e.target.value;
                field.onChange(v === '' ? undefined : Number.parseInt(v, 10));
              }}
              onBlur={field.onBlur}
              name={field.name}
              errors={form.formState.errors as FieldErrors}
              className="with-subtext legal-entity-select"
            />
          )}
        />
      </div>
      <div className="signup-form-row">
        <Controller
          control={form.control}
          name="IsPublicEntity"
          rules={{
            validate: (val) => val !== undefined || 'Company Type is required.',
          }}
          render={({ field }) => (
            <Select
              label="Company Type"
              placeholder="Select..."
              options={COMPANY_TYPE_OPTIONS}
              value={field.value === undefined ? '' : String(field.value)} // <-- IMPORTANT
              onChange={(e) => {
                const v = e.target.value;
                field.onChange(v === '' ? undefined : v === 'true');
              }}
              onBlur={field.onBlur}
              name={field.name}
              className="with-subtext"
              errors={form.formState.errors}
            />
          )}
        />
        <FormTextField
          label="Website URL"
          {...form.register('Url', {
            ...requiredText('Website URL'),
            ...maxLengthRule(150, 'Website URL'),
            validate: {
              isValidUrl: (value) => {
                if (!value || typeof value !== 'string' || value.trim().length === 0) {
                  return 'Website URL is required.';
                }
                return isURL(value) || 'Website URL is invalid.';
              },
            },
            ...trimString,
          })}
          errors={form.formState.errors.Url}
          className="with-subtext"
          autoComplete="url"
        />
      </div>
      {isPublicEntity && (
        <div className="signup-form-row">
          <FormTextField
            label="Stock Exchange"
            {...form.register('StockExchange', {
              ...requiredText('Stock Exchange'),
              ...maxLengthRule(100, 'Stock Exchange'),
              ...trimString,
            })}
            errors={form.formState.errors.StockExchange}
            className="with-subtext"
          />
          <FormTextField
            label="Stock Ticker"
            {...form.register('StockTicker', {
              ...requiredText('Stock Ticker'),
              ...maxLengthRule(20, 'Stock Ticker'),
              ...trimString,
            })}
            errors={form.formState.errors.StockTicker}
            className="with-subtext"
          />
        </div>
      )}
      <div className="signup-form-row">
        <Controller
          control={form.control}
          name="AccountCategoryId"
          rules={{
            ...requiredText('Selection'),
          }}
          render={({ field }) => (
            <Select
              label="Which best describes your organization?"
              placeholder="Select..."
              options={categoryOptions}
              value={field.value === undefined || field.value === null ? '' : String(field.value)}
              onChange={(e) => {
                const v = e.target.value;
                field.onChange(v === '' ? undefined : Number.parseInt(v, 10));
              }}
              onBlur={field.onBlur}
              name={field.name}
              errors={form.formState.errors as FieldErrors}
              className="with-subtext"
            />
          )}
        />
        <Controller
          control={form.control}
          name="HasAcceptedCreditCards"
          rules={{
            validate: (val) => val !== undefined || 'Selection is required.',
          }}
          render={({ field }) => (
            <Select
              label="Has your company accepted credit cards in the past?"
              placeholder="Select..."
              options={YES_NO_OPTIONS}
              value={field.value === undefined ? '' : String(field.value)}
              onChange={(e) => {
                const v = e.target.value;
                field.onChange(v === '' ? undefined : v === 'true');
              }}
              onBlur={field.onBlur}
              name={field.name}
              className="with-subtext"
              errors={form.formState.errors as FieldErrors}
            />
          )}
        />
      </div>
    </div>
  );
};
